import React, { useEffect, useRef } from "react";

/** Nuclear-fission chain reaction visualization.
 *  - Atoms are Bohr-model rings: red protons + blue neutrons in the nucleus,
 *    cyan electrons on the outer ring.
 *  - A neutron (bright head + fading trail) travels toward an atom and, on
 *    impact, scatters ALL its dots into free particles with impulse velocity.
 *  - Free particles keep drifting with drag; they never "return" to their
 *    original atom. Instead, every so often the field gathers a batch of
 *    nearby free particles into a NEW atom at a random spot — potentially
 *    with a fresh mix of red / blue / cyan.
 *  - Neutrons fan out from every impact, so the chain keeps flowing.
 *  Pure canvas + rAF. Auto-disables on reduced-motion. */
export default function FissionField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0, height = 0;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rand = (a, b) => a + Math.random() * (b - a);
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    /** Build a fresh atom (Bohr-style) at (cx, cy).
     *  Optionally seeded with an array of pre-existing particles (from a
     *  reassembly gather) — their color/role gets preserved when placed. */
    const makeAtom = (cx, cy, seeds = null) => {
      const dots = [];
      const ringN = 6 + Math.floor(Math.random() * 3);
      const ringR = rand(28, 40);
      const shells = [
        { r: 0,  count: 1 },
        { r: 8,  count: 6 },
        { r: 15, count: 10 },
      ];
      const slots = [];
      // Electron slots
      for (let i = 0; i < ringN; i++) {
        const angle = (i / ringN) * Math.PI * 2 + Math.random() * 0.15;
        slots.push({ role: "electron", angle, r: ringR, size: rand(3.5, 5), color: "cyan" });
      }
      // Nucleon slots (colors alternate red/blue by default, but if seeds are
      // supplied we adopt their colors so reassembled atoms show a fresh mix).
      let idx = 0;
      for (const sh of shells) {
        for (let i = 0; i < sh.count; i++) {
          const angle = sh.count === 1 ? 0 : (i / sh.count) * Math.PI * 2 + rand(-0.1, 0.1);
          const rr = sh.r + rand(-1.5, 1.5);
          slots.push({
            role: "nucleon", angle, r: rr,
            size: rand(4, 5.5),
            color: (idx % 2 === 0) ? "red" : "blue",
          });
          idx++;
        }
      }
      // Match seed particles to slots by role compatibility; leftovers get defaults.
      const availableElectrons = seeds ? seeds.filter(p => p.role === "electron") : [];
      const availableNucleons  = seeds ? seeds.filter(p => p.role === "nucleon")  : [];
      for (const s of slots) {
        const pool = s.role === "electron" ? availableElectrons : availableNucleons;
        const seed = pool.length ? pool.shift() : null;
        const x = cx + Math.cos(s.angle) * s.r;
        const y = cy + Math.sin(s.angle) * s.r;
        dots.push({
          role: s.role,
          angle: s.angle, r: s.r,
          x: seed ? seed.x : x,
          y: seed ? seed.y : y,
          vx: 0, vy: 0,
          size: seed ? seed.size : s.size,
          color: seed ? seed.color : s.color,
          gatherTx: x, gatherTy: y,
          gathering: !!seed,
          gatherTicks: seed ? 40 : 0,
        });
      }
      return {
        cx, cy, r: ringR, dots,
        spin: rand(-0.006, 0.006),
        nucleusSpin: rand(-0.004, 0.004),
        state: seeds ? "gathering" : "stable",
        hueShift: seeds ? 0.6 : 0,
      };
    };

    const seedAtoms = () => {
      const area = width * height;
      const target = Math.max(5, Math.min(11, Math.round(area / 170000)));
      const list = [];
      const minGap = 170;
      let attempts = 0;
      while (list.length < target && attempts < 240) {
        attempts++;
        const x = rand(90, width - 90);
        const y = rand(130, height - 130);
        if (list.every(a => Math.hypot(a.cx - x, a.cy - y) > minGap)) {
          list.push(makeAtom(x, y));
        }
      }
      return list;
    };
    let atoms = seedAtoms();
    let free = [];  // freeParticle list

    let lastArea = width * height;
    const onResize = () => {
      resize();
      const area = width * height;
      if (Math.abs(area - lastArea) / lastArea > 0.25) {
        atoms = seedAtoms(); free = [];
        lastArea = area;
      }
    };
    window.removeEventListener("resize", resize);
    window.addEventListener("resize", onResize);

    const neutrons = [];
    const spawnNeutron = (x, y, target) => {
      const dx = target.cx - x;
      const dy = target.cy - y;
      const dist = Math.hypot(dx, dy) || 1;
      const speed = rand(1.6, 2.4);
      // Pick a trail color from the target atom's own dots so the streak matches
      // whatever it's aiming at (red / blue / cyan) instead of a yellow flare.
      const palette = ["red", "blue", "cyan"];
      const color = target?.dots?.length
        ? pick(target.dots).color
        : pick(palette);
      neutrons.push({
        x, y,
        vx: (dx / dist) * speed,
        vy: (dy / dist) * speed,
        target,
        color,
        trail: [],
        life: 0, maxLife: 600,
      });
    };

    const spark = () => {
      const stable = atoms.filter(a => a.state === "stable");
      if (!stable.length) return;
      const t = pick(stable);
      const side = Math.floor(Math.random() * 4);
      let x, y;
      if (side === 0)      { x = -30;         y = rand(50, height - 50); }
      else if (side === 1) { x = width + 30;  y = rand(50, height - 50); }
      else if (side === 2) { x = rand(0, width); y = -30; }
      else                 { x = rand(0, width); y = height + 30; }
      spawnNeutron(x, y, t);
    };
    setTimeout(spark, 300);

    /** Try to gather free particles into a new atom at a fresh spot. */
    const REASSEMBLE_MIN_ELECTRONS = 5;
    const REASSEMBLE_MIN_NUCLEONS  = 12;
    let lastReassemble = performance.now();
    const tryReassemble = () => {
      const electrons = free.filter(p => p.role === "electron");
      const nucleons  = free.filter(p => p.role === "nucleon");
      if (electrons.length < REASSEMBLE_MIN_ELECTRONS) return;
      if (nucleons.length  < REASSEMBLE_MIN_NUCLEONS)  return;
      // Random spot with min gap from existing atoms
      let x, y, ok = false;
      for (let tries = 0; tries < 40 && !ok; tries++) {
        x = rand(80, width - 80);
        y = rand(120, height - 120);
        ok = atoms.every(a => Math.hypot(a.cx - x, a.cy - y) > 170);
      }
      if (!ok) return;
      // Grab nearest particles to that spot
      electrons.sort((a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y));
      nucleons.sort((a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y));
      const usedE = electrons.slice(0, 8);
      const usedN = nucleons.slice(0, 17);
      const usedSet = new Set([...usedE, ...usedN]);
      free = free.filter(p => !usedSet.has(p));
      atoms.push(makeAtom(x, y, [...usedE, ...usedN]));
    };

    /** Convert an atom's dots into free particles with impulse velocities. */
    const explode = (atom) => {
      for (const d of atom.dots) {
        const angle = Math.atan2(d.y - atom.cy, d.x - atom.cx) || rand(0, Math.PI * 2);
        const s = rand(1.6, 3.0);
        free.push({
          x: d.x, y: d.y,
          vx: Math.cos(angle) * s,
          vy: Math.sin(angle) * s,
          size: d.size,
          color: d.color,
          role: d.role,
          life: 0, maxLife: 900,
        });
      }
      // Remove the atom from the atoms array.
      atoms = atoms.filter(a => a !== atom);
    };

    // Drawing helpers ---------------------------------------------------------
    const COLOR_MAP = {
      red:  { halo: "rgba(248, 113, 113, 0.20)", core: "rgba(252, 165, 165, 0.95)", trail: "248, 113, 113" },
      blue: { halo: "rgba(96, 165, 250, 0.20)",  core: "rgba(147, 197, 253, 0.95)", trail: "96, 165, 250"  },
      cyan: { halo: "rgba(34, 211, 238, 0.18)",  core: "rgba(103, 232, 249, 0.90)", trail: "34, 211, 238"  },
    };
    const drawDot = (x, y, size, color) => {
      const c = COLOR_MAP[color] || COLOR_MAP.cyan;
      ctx.fillStyle = c.halo;
      ctx.beginPath(); ctx.arc(x, y, size + 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = c.core;
      ctx.beginPath(); ctx.arc(x, y, size, 0, Math.PI * 2); ctx.fill();
    };

    let raf, lastSpark = performance.now();
    const step = () => {
      // Hard-clear each frame so no yellow/translucent trails linger.
      ctx.clearRect(0, 0, width, height);

      // ---------- atoms ----------
      for (const atom of atoms) {
        if (atom.state === "gathering") {
          let settled = 0;
          for (const d of atom.dots) {
            if (d.gathering) {
              d.x += (d.gatherTx - d.x) * 0.10;
              d.y += (d.gatherTy - d.y) * 0.10;
              d.gatherTicks--;
              if (Math.hypot(d.gatherTx - d.x, d.gatherTy - d.y) < 1.2 || d.gatherTicks <= 0) {
                d.gathering = false;
              }
            }
            if (!d.gathering) settled++;
          }
          atom.hueShift = Math.max(0, atom.hueShift - 0.02);
          if (settled === atom.dots.length) atom.state = "stable";
        } else {
          for (const d of atom.dots) {
            d.angle += d.role === "electron" ? atom.spin : atom.nucleusSpin;
            d.x = atom.cx + Math.cos(d.angle) * d.r;
            d.y = atom.cy + Math.sin(d.angle) * d.r;
          }
        }

        // Electron ring bonds
        const electrons = atom.dots.filter(d => d.role === "electron");
        ctx.strokeStyle = "rgba(34, 211, 238, 0.20)";
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        for (let i = 0; i < electrons.length; i++) {
          const a = electrons[i];
          const b = electrons[(i + 1) % electrons.length];
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
        }
        ctx.stroke();

        for (const d of atom.dots) drawDot(d.x, d.y, d.size, d.color);
      }

      // ---------- free particles ----------
      for (let i = free.length - 1; i >= 0; i--) {
        const p = free[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.995;   // gentle drag
        p.vy *= 0.995;
        p.life++;

        // Soft edge-bounce so particles stay on stage
        if (p.x < 20)         { p.vx += 0.05; }
        if (p.x > width - 20) { p.vx -= 0.05; }
        if (p.y < 20)         { p.vy += 0.05; }
        if (p.y > height - 20){ p.vy -= 0.05; }

        // Fade near end of life
        const fade = p.life > p.maxLife - 120
          ? Math.max(0, 1 - (p.life - (p.maxLife - 120)) / 120)
          : 1;
        ctx.save();
        ctx.globalAlpha = fade;
        drawDot(p.x, p.y, p.size, p.color);
        ctx.restore();
        if (p.life > p.maxLife) free.splice(i, 1);
      }

      // ---------- neutrons ----------
      for (let i = neutrons.length - 1; i >= 0; i--) {
        const n = neutrons[i];
        n.trail.unshift({ x: n.x, y: n.y });
        if (n.trail.length > 18) n.trail.pop();
        n.x += n.vx;
        n.y += n.vy;
        n.life++;

        ctx.lineWidth = 1.4;
        const trailRGB = (COLOR_MAP[n.color] || COLOR_MAP.cyan).trail;
        for (let j = 0; j < n.trail.length - 1; j++) {
          const alpha = 0.75 * (1 - j / n.trail.length);
          ctx.strokeStyle = `rgba(${trailRGB}, ${alpha})`;
          ctx.beginPath();
          ctx.moveTo(n.trail[j].x, n.trail[j].y);
          ctx.lineTo(n.trail[j + 1].x, n.trail[j + 1].y);
          ctx.stroke();
        }
        // Head: same-color radial glow (no more yellow flare).
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, 14);
        grad.addColorStop(0,   `rgba(${trailRGB}, 0.95)`);
        grad.addColorStop(0.5, `rgba(${trailRGB}, 0.35)`);
        grad.addColorStop(1,   `rgba(${trailRGB}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath(); ctx.arc(n.x, n.y, 14, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(${trailRGB}, 1)`;
        ctx.beginPath(); ctx.arc(n.x, n.y, 2.8, 0, Math.PI * 2); ctx.fill();

        if (n.target && n.target.state === "stable" && atoms.includes(n.target)) {
          const d = Math.hypot(n.x - n.target.cx, n.y - n.target.cy);
          if (d < n.target.r + 4) {
            const tgt = n.target;
            // Spawn 1-2 new neutrons to other stable atoms BEFORE exploding
            // (so we can still reference tgt's centre).
            const others = atoms.filter(a => a !== tgt && a.state === "stable");
            if (others.length) {
              const fan = 1 + (Math.random() < 0.55 ? 1 : 0);
              const used = new Set();
              for (let k = 0; k < fan && used.size < others.length; k++) {
                let next; let tries = 0;
                do { next = pick(others); tries++; }
                while (used.has(next) && tries < 6);
                used.add(next);
                spawnNeutron(tgt.cx, tgt.cy, next);
              }
            }
            explode(tgt);
            neutrons.splice(i, 1);
            continue;
          }
        }

        if (n.x < -60 || n.x > width + 60 || n.y < -60 || n.y > height + 60 || n.life > n.maxLife) {
          neutrons.splice(i, 1);
        }
      }

      // ---------- reassembly (throttled) ----------
      const now = performance.now();
      if (now - lastReassemble > 1600) {
        tryReassemble();
        lastReassemble = now;
      }

      // Re-spark chain when quiet
      if (neutrons.length === 0 && now - lastSpark > 1400) {
        spark();
        lastSpark = now;
      } else if (neutrons.length > 0) {
        lastSpark = now;
      }

      // Safety: if the field has been emptied entirely, re-seed a couple.
      if (atoms.length === 0 && free.length < 15) {
        atoms.push(makeAtom(rand(80, width - 80), rand(130, height - 130)));
        atoms.push(makeAtom(rand(80, width - 80), rand(130, height - 130)));
      }

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const onVis = () => {
      if (document.hidden) { cancelAnimationFrame(raf); raf = null; }
      else if (!raf) { raf = requestAnimationFrame(step); }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="sat-fission"
      aria-hidden
      data-testid="fission-field"
    />
  );
}
